<template>
  <section v-if="eligible" class="mt-6 rounded-xl border border-gray-200 p-5 space-y-4">
    <div>
      <h3 class="text-lg font-semibold">Booking payments</h3>
    </div>
    <p class="text-sm">Choose what customers pay when they book on your website. Existing bookings are eligible for payment requests only if revenue has not been added and their appointment date has not passed.</p>
    <p v-if="busy && !loaded" role="status" class="text-sm text-gray-500">Loading payment settings…</p>
    <button v-else-if="!loaded" type="button" class="rounded-lg border px-4 py-2" @click="load">Retry loading settings</button>
    <form v-if="loaded" class="space-y-4" @submit.prevent="save">
      <label class="block font-medium">Payment at booking
        <select v-model="policy.mode" :disabled="busy" class="mt-2 block w-full rounded-lg border border-gray-300 p-3 text-gray-900">
          <option value="deposit">Collect a deposit</option><option value="full">Collect full payment</option>
          <option value="pay_later">Pay later — no payment at booking</option>
        </select>
      </label>
      <p class="text-sm text-gray-500">{{ policy.mode === 'deposit' ? 'Customers acknowledge the deposit before booking. Their booking is created first, then they pay the deposit through your existing deposit flow. The remaining balance is collected later.' : policy.mode === 'full' ? 'Customers acknowledge the full payment before booking. Their booking is created first, then they pay the full total, including applicable taxes.' : 'Customers can confirm their booking without paying upfront.' }}</p>
      <div v-if="policy.mode === 'deposit'" class="grid gap-4 sm:grid-cols-2">
        <label class="block font-medium">Deposit type
          <select v-model="policy.depositType" :disabled="busy" class="mt-2 block w-full rounded-lg border border-gray-300 p-3 text-gray-900">
            <option value="percentage">Percentage</option><option value="fixed">Fixed amount</option>
          </select>
        </label>
        <label class="block font-medium">{{ policy.depositType === 'fixed' ? 'Deposit amount ($)' : 'Deposit percentage (%)' }}
          <input v-model.number="depositDisplay" :disabled="busy" type="number" required min="0.01" :max="policy.depositType === 'percentage' ? 100 : undefined" step="0.01" class="mt-2 block w-full rounded-lg border border-gray-300 p-3 text-gray-900" />
        </label>
      </div>
      <p class="text-sm text-gray-500">Deposit amounts use your existing Square deposit settings. Changes here also apply to your current deposit requests.</p>
      <details class="rounded-lg border p-4">
        <summary class="cursor-pointer font-medium">Deposit requests and reminders</summary>
        <div class="mt-4 space-y-3">
          <label class="flex items-center gap-2"><input v-model="depositPreferences.square_deposit_disclaimer" :disabled="busy" type="checkbox" /> Show deposit information on the landing page</label>
          <DepositAutomationSettings v-if="depositPreferences.square_deposit" v-model="depositPreferences.depositAutomation" />
        </div>
      </details>
      <p v-if="checkoutEnabled" class="rounded-lg bg-green-50 p-3 text-sm text-green-900">Payment collection is active. Your saved payment preference applies to new website bookings.</p>
      <p v-else class="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Payment collection is awaiting activation. You can save your preference now; saving does not start charging customers.</p>
      <button type="submit" :disabled="busy" class="rounded-lg bg-gray-900 px-4 py-2 font-medium text-white disabled:opacity-50">{{ busy ? 'Saving…' : 'Save payment settings' }}</button>
    </form>
    <p v-if="message" role="status" class="text-sm">{{ message }}</p>
  </section>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebase/config';
import store from '../../../store';
import DepositAutomationSettings from './DepositAutomationSettings.vue';

const eligible = computed(() => store.state.userConfig?.project === 'shadow' && store.state.userConfig?.profile === 's3Tint-invXT2bn');
const policy = ref({ mode: 'pay_later', depositType: 'percentage', depositValue: 25 });
const depositPreferences = ref({ square_deposit: false, square_deposit_default: false, square_deposit_disclaimer: true,
  depositAutomation: { enabled: false, reminderHours: 2, cancellationHours: 2 } });
const depositDisplay = computed({
  get: () => policy.value.depositType === 'fixed' ? policy.value.depositValue / 100 : policy.value.depositValue,
  set: value => { policy.value.depositValue = policy.value.depositType === 'fixed' ? Math.round(Number(value) * 100) : Number(value); },
});
const loaded = ref(false);
const checkoutEnabled = ref(false);
const busy = ref(false);
const message = ref('');
function paymentErrorMessage(error) {
  if (error?.code === 'functions/unauthenticated') return 'Your session has expired. Please sign in again.';
  if (error?.code === 'functions/permission-denied') return 'Only the shop owner can manage these payment settings.';
  if (error?.code === 'functions/invalid-argument') return 'Please check the payment settings and try again.';
  return 'Payment settings are temporarily unavailable. Please try again. Your saved settings have not changed.';
}
async function request(action) {
  busy.value = true;
  message.value = '';
  try {
    const result = await httpsCallable(functions, 'shadowPaymentPolicy')({ action, policy: policy.value, depositPreferences: depositPreferences.value });
    policy.value = result.data.policy;
    checkoutEnabled.value = result.data.checkoutEnabled === true;
    if (result.data.depositPreferences) depositPreferences.value = result.data.depositPreferences;
    loaded.value = true;
    if (action === 'save') message.value = checkoutEnabled.value
      ? 'Settings saved. Your payment preference now applies to new website bookings.'
      : 'Settings saved. Your existing deposit configuration is updated. Payment at booking will apply when embedded collection is activated.';
  } catch (error) {
    message.value = paymentErrorMessage(error);
  } finally {
    busy.value = false;
  }
}
const load = () => request('get');
const save = () => request('save');
onMounted(() => { if (eligible.value) load(); });
</script>
